import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";
import { useCurrentUser } from "./useCurrentUser";

export type ProfileRow = Tables<"profiles">;

type BankDetails = Pick<ProfileRow, "bank_name" | "account_number" | "account_holder" | "branch_code" | "account_type">;

export interface ProfileStats {
  profile: ProfileRow | null;
  followers: number;
  following: number;
  views: number;
}

/**
 * Single source of truth for the signed-in user's profile.
 * Every surface (sidebar, profile page, top nav) reads this same query key,
 * so a save on the profile page instantly refreshes the whole app.
 */
export function useProfile() {
  const { userId } = useCurrentUser();
  const qc = useQueryClient();

  // Keep plan state fresh: the Paystack webhook flips subscription_status
  // server-side, so listen for the row change instead of waiting for a reload.
  useEffect(() => {
    if (!userId) return;

    // A profile hook can be mounted by several dashboard surfaces at once.
    // Use a collision-proof topic so the client never hands a new listener an
    // already-subscribed channel (including after a Vite hot reload).
    const subscriptionId = globalThis.crypto?.randomUUID?.()
      ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const channel = supabase
      .channel(`profile-${userId}-${subscriptionId}`)
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "profiles", filter: `id=eq.${userId}` },
        () => qc.invalidateQueries({ queryKey: ["profile", userId] }),
      )
      .subscribe();

    return () => {
      // Unsubscribe synchronously before removal so a remount cannot append
      // callbacks to a channel that is still in the subscribed state.
      void channel.unsubscribe();
      void supabase.removeChannel(channel);
    };
  }, [userId, qc]);

  const query = useQuery({
    queryKey: ["profile", userId],
    enabled: !!userId,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    queryFn: async (): Promise<ProfileStats> => {
      const [profileRes, bankRes, followersRes, followingRes] = await Promise.all([
        supabase
          .from("profiles")
          .select(
            "id, username, full_name, avatar_url, bio, created_at, updated_at, user_type, verification_status, verification_submitted_at, verified_at, city, country, business_name, subscription_status, latitude, longitude, is_live, last_active_at, profile_views, theme_preference, upgraded_at, portal_id, website, social_links, gallery, pricing",
          )
          .eq("id", userId!)
          .maybeSingle(),
        // Banking columns are private: only the owner can read them, via this function.
        (supabase.rpc as unknown as (fn: string) => Promise<{ data: BankDetails[] | null }>)("get_my_bank_details"),
        supabase.from("follows").select("id", { count: "exact", head: true }).eq("following_id", userId!),
        supabase.from("follows").select("id", { count: "exact", head: true }).eq("follower_id", userId!),
      ]);
      if (profileRes.error) throw profileRes.error;
      const bank = Array.isArray(bankRes.data) ? bankRes.data[0] : null;
      return {
        profile: profileRes.data ? ({ ...profileRes.data, ...(bank ?? {}) } as ProfileRow) : null,
        followers: followersRes.count ?? 0,
        following: followingRes.count ?? 0,
        views: profileRes.data?.profile_views ?? 0,
      };
    },
  });

  return { userId, ...query };
}

export function useUpdateProfile() {
  const qc = useQueryClient();
  const { userId } = useCurrentUser();

  return useMutation({
    mutationFn: async (patch: Partial<ProfileRow>) => {
      if (!userId) throw new Error("You need to be signed in.");
      const { data, error } = await supabase
        .from("profiles")
        .update(patch)
        .eq("id", userId)
        .select("id, username, full_name, avatar_url, bio, user_type, city, country, business_name, theme_preference, portal_id, website, social_links, gallery, pricing")
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["profile"] });
      qc.invalidateQueries({ queryKey: ["profiles"] });
      qc.invalidateQueries({ queryKey: ["directory"] });
    },
  });
}

export function displayName(p: ProfileRow | null | undefined) {
  return p?.business_name || p?.full_name || (p?.username ? `@${p.username}` : "Your profile");
}
